import React, { memo, MouseEventHandler, useState } from 'react';
import { Company, User, UserType } from '@app/models';
import { Button } from 'components/ui/button';
import { useUserContext } from '@app/providers';
import { IconEmail16, IconPhone16, IconWhatsApp16 } from '@app/icons';
import { ModalProfile } from '@app/modals';
import { EntityUserInfo, IndividualUserInfo } from './components';
import { ProfileAvatar } from 'pages/Profile/components';
import { cn } from 'lib/utils';

interface Props {
  author: User;
  company?: Company | null;
  className?: string;
  showContacts?: boolean;
}

function AuthorSection(props: Props) {
  const { author, company, className = '', showContacts = true } = props;
  const { user } = useUserContext();
  const [showModal, setShowModal] = useState<boolean>(false);
  const onShowAboutUser: MouseEventHandler<HTMLButtonElement> = () => {
    setShowModal(true);
  };
  return (
    <section
      className={cn(
        'pt- p-2 sm:p-4 lg:p-6',
        // 'rounded-xl bg-muted/40 py-3 sm:py-4 w-full mx-0 sm:mx-auto px-3 sm:px-6 lg:px-8 max-w-7xl',
        className
      )}
    >
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
          <div className="flex-shrink-0">
            {company && author.type === UserType.ENTITY ? (
              <ProfileAvatar
                size={80}
                avatarFiles={
                  company?.avatarFilesIds
                    ? company.avatarFilesIds.split(',')[0]
                    : ''
                }
              />
            ) : (
              <ProfileAvatar
                size={80}
                avatarFiles={
                  author?.avatarFilesIds
                    ? author.avatarFilesIds.split(',')[0]
                    : ''
                }
              />
            )}
            <ModalProfile
              user={author}
              company={company}
              onClose={() => setShowModal(false)}
              visible={showModal}
            />
          </div>
          <div className="flex-1 min-w-0 text-center sm:text-left">
            {company && author.type === UserType.ENTITY ? (
              <EntityUserInfo author={author} company={company} />
            ) : (
              <IndividualUserInfo author={author} />
            )}
          </div>
        </div>
        {showContacts && user.authenticated && (
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-stretch sm:items-center mt-2">
            <Button
              onClick={onShowAboutUser}
              size="sm"
              variant="outline"
              className="w-full sm:w-auto flex-shrink-0"
            >
              Подробнее
            </Button>
            <div className="flex gap-2 justify-center sm:justify-start flex-wrap">
              {author?.email && (
                <a
                  className="flex-1 sm:flex-none inline-flex items-center justify-center rounded px-4 py-3 sm:p-2 hover:bg-accent transition border sm:border-0 min-w-[80px]"
                  href={`mailto:${author.email}`}
                  title="Email"
                >
                  <IconEmail16 className="flex-shrink-0" />
                  <span className="ml-2 sm:hidden text-sm whitespace-nowrap">
                    Email
                  </span>
                </a>
              )}
              {author?.whatsAppNumber && (
                <a
                  className="flex-1 sm:flex-none inline-flex items-center justify-center rounded px-4 py-3 sm:p-2 hover:bg-accent transition border sm:border-0 min-w-[80px]"
                  href={`https://api.whatsapp.com/send?phone=${author.whatsAppNumber}`}
                  title="WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconWhatsApp16 className="flex-shrink-0" />
                  <span className="ml-2 sm:hidden text-sm whitespace-nowrap">
                    WhatsApp
                  </span>
                </a>
              )}
              {author?.phone && (
                <a
                  className="flex-1 sm:flex-none inline-flex items-center justify-center rounded px-4 py-3 sm:p-2 hover:bg-accent transition border sm:border-0 min-w-[80px]"
                  href={`tel:${author.phone}`}
                  title="Телефон"
                >
                  <IconPhone16 className="flex-shrink-0" />
                  <span className="ml-2 sm:hidden text-sm whitespace-nowrap">
                    Телефон
                  </span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default memo(AuthorSection);
